import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'

const FAQ = () => {
  const [openItems, setOpenItems] = useState({})

  const toggleItem = (index) => {
    setOpenItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  const faqCategories = [
    {
      title: 'For Clients',
      items: [
        {
          question: 'How do I book my photographer?',
          answer: 'Our team assigns a professional photographer/videographer based on your location, style, and availability.'
        },
        {
          question: 'What if I need to cancel my booking?',
          answer: 'All purchases are final and non-refundable, so please review your booking details carefully before completing your purchase.'
        },
        {
          question: 'How do I receive my photos?',
          answer: 'After your event, we typically deliver edited photos within 1-3 months through a secure online gallery. You’ll receive high-resolution downloads and can order prints if you’d like.'
        },
        {
          question: 'Are the photographers insured?',
          answer: 'Photographers on our platform are not required to carry liability insurance. We recommend that couples add it to their package as an optional add-on for peace of mind, as some venues may require it.'
        },
        {
          question: 'Can I request specific shots or styles?',
          answer: 'Absolutely! You can discuss your vision, shot list, and style preferences directly with your photographer one month before the event via online chat. They\'ll work with you to capture exactly what you envision.'
        },
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit cards, debit cards, Cash App, Affirm, and Klarna. All payments are processed securely through our encrypted payment system.'
        },
        {
          question: 'How are you so affordable?',
          answer: 'We partner with talented photographers who are building their careers and perfecting their craft. They’re in that exciting stage of growing their portfolios before opening their own studios, where the same coverage often runs $3 to $5K. It’s a win for everyone. You get stunning photos at one of the best prices in the country, and our photographers get to do what they love while advancing their careers.'
        }
      ]
    },
    {
      title: 'For Photographers',
      items: [
        {
          question: 'How do I join Love & Photos?',
          answer: 'Click "Join as Photographer" and complete our application. We\'ll review your portfolio, verify your credentials, then onboard you with training materials to get started.'
        },
        {
          question: 'How do I earn the “Love & Photos Choice” label?',
          answer: 'This label is awarded by our team to photographers and videographers who consistently deliver outstanding service and exceptional quality.'
        },
        {
          question: 'How do I get paid?',
          answer: 'Payments are released within 14 business days after all media has been uploaded. They are processed through QuickBooks Online to your bank account.'
        },
        {
          question: 'What support do you provide?',
          answer: 'We offer 24/7 support and a variety of educational resources. There is also a community forum where photographers can connect and share ideas. (coming soon)'
        },
        {
          question: 'How do I improve my visibility?',
          answer: 'Complete your profile, maintain high ratings, and respond quickly to bookings. Keep your portfolio fresh with your latest wedding work to stand out and attract more couples.'
        }
      ]
    },
    {
      title: 'Platform & Policies',
      items: [
        {
          question: 'Is Love & Photos available in my area?',
          answer: 'We currently operate in all 50 states. Enter your zip code when browsing to see photographers in your area.'
        },
        {
          question: 'How do you vet photographers?',
          answer: 'All photographers undergo portfolio review, credential verification, and are vetted by our team. We continuously monitor performance through client feedback and ratings.'
        },
        {
          question: 'How does the review system work?',
          answer: 'After each event, clients can rate photographers on professionalism, quality, and communication. Reviews are permanent and help maintain our quality standards.'
        },
        {
          question: 'What\'s your privacy policy?',
          answer: 'We take privacy seriously. Personal information is encrypted, never sold to third parties, and only shared as necessary for booking and payment processing. See our full privacy policy for details.'
        }
      ]
    }
  ]

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Find answers to common questions about Love & Photos.
            Can't find what you're looking for? Contact our support team.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {faqCategories.map((category, categoryIndex) => (
            <div key={categoryIndex} className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-6">
                {category.title}
              </h2>
              <div className="space-y-4">
                {category.items.map((item, itemIndex) => {
                  const globalIndex = `${categoryIndex}-${itemIndex}`
                  const isOpen = openItems[globalIndex]

                  return (
                    <div
                      key={itemIndex}
                      className="bg-card rounded-lg overflow-hidden border"
                    >
                      <button
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-accent transition"
                        onClick={() => toggleItem(globalIndex)}
                      >
                        <span className="font-semibold text-foreground">
                          {item.question}
                        </span>
                        {isOpen ? (
                          <ChevronUp className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-4">
                          <p className="text-muted-foreground">{item.answer}</p>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-6">
            Still Have Questions?
          </h2>
          <p className="text-muted-foreground mb-8">
            Our support team is here to help. Reach out anytime and we'll get back to you within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:support@loveandphotos.com"
              className="px-6 py-3 bg-card text-foreground font-semibold rounded-lg hover:bg-accent transition border"
            >
              Email Support
            </a>
            <a
              href="/contact"
              className="px-6 py-3 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-primary/90 transition"
            >
              Contact Form
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default FAQ